<script setup lang="ts">
import { computed, useSlots, h, mergeProps, cloneVNode } from "vue";
import { cn } from "@/utils/cn";

const emit = defineEmits(["click"]);

interface Props {
  variant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link";
  size?: "default" | "sm" | "lg" | "icon";
  type?: "button" | "submit" | "reset";
  disabled?: boolean;
  asChild?: boolean;
  class?: string;
}

const props = withDefaults(defineProps<Props>(), {
  variant: "default",
  size: "default",
  type: "button",
  disabled: false,
  asChild: false,
});

const classes = computed(() =>
  cn(
    'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
    {
      'bg-primary text-primary-foreground shadow hover:bg-primary/90': props.variant === 'default',
      'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90': props.variant === 'destructive',
      'border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground': props.variant === 'outline',
      'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80': props.variant === 'secondary',
      'hover:bg-accent hover:text-accent-foreground': props.variant === 'ghost',
      'text-primary underline-offset-4 hover:underline': props.variant === 'link',
      'h-9 px-4 py-2': props.size === 'default',
      'h-8 rounded-md px-3 text-xs': props.size === 'sm',
      'h-10 rounded-md px-8': props.size === 'lg',
      'h-9 w-9': props.size === 'icon',
    },
    props.class
  )
);

const slots = useSlots();

const vnode = computed(() => {
  if (!props.asChild) {
    return h(
      "button",
      {
        type: props.type,
        disabled: props.disabled,
        class: classes.value,
        onClick: (e: Event) => emit("click", e),
      },
      slots.default?.()
    );
  }

  const defaultSlot = slots.default?.();
  if (!defaultSlot || defaultSlot.length === 0) return null;
  const firstChild = defaultSlot[0];
  return cloneVNode(firstChild, mergeProps(firstChild.props || {}, { class: classes.value }));
});
</script>

<template>
  <component :is="vnode" />
</template>
